
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const location = useLocation();

  // 初期化時にテーマを設定
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    const shouldBeDark = savedTheme === 'dark' || (!savedTheme && systemPrefersDark);
    setIsDarkMode(shouldBeDark);
    
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // システムの色設定変更を監視
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('theme')) {
        setIsDarkMode(e.matches);
        if (e.matches) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Links', href: '/links' },
    { name: 'Blog', href: '/blog' },
    { name: 'Calendar', href: '/calendar' },
    { name: 'Forum', href: '/forum' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' }
  ];

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <i className="ri-information-line text-white text-lg"></i>
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">斎藤元彦まとめ</span>
            </Link>
          </div>

          <nav className="hidden md:flex space-x-8">
            <Link to="/" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 px-3 py-2 rounded-md text-base font-semibold">
              ホーム
            </Link>
            <Link to="/verification" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 px-3 py-2 rounded-md text-base font-semibold">
              検証データ
            </Link>
            <Link to="/blog" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 px-3 py-2 rounded-md text-base font-semibold">
              記事
            </Link>
            <Link to="/links" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 px-3 py-2 rounded-md text-base font-semibold">
              リンク
            </Link>
            <Link to="/forum" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 px-3 py-2 rounded-md text-base font-semibold">
              掲示板
            </Link>
            <Link to="/admin" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 px-3 py-2 rounded-md text-base font-semibold">
              管理画面
            </Link>
          </nav>

          <div className="flex items-center space-x-4">
            <button
              onClick={toggleDarkMode}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer"
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-lg`}></i>
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <Link to="/" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 block px-3 py-2 rounded-md text-base font-semibold">
                ホーム
              </Link>
              <Link to="/verification" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 block px-3 py-2 rounded-md text-base font-semibold">
                検証データ
              </Link>
              <Link to="/blog" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 block px-3 py-2 rounded-md text-base font-semibold">
                記事
              </Link>
              <Link to="/links" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 block px-3 py-2 rounded-md text-base font-semibold">
                リンク
              </Link>
              <Link to="/forum" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 block px-3 py-2 rounded-md text-base font-semibold">
                掲示板
              </Link>
              <Link to="/admin" className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-300 block px-3 py-2 rounded-md text-base font-semibold">
                管理画面
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
